import React, { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell, LabelList } from 'recharts';

export interface PendingAtBarChartProps {
  data: Array<{
    name: string;
    fullDate: string;
    completed: number;
    pendingClient: number;
    pendingDM: number;
    pendingConsultant: number;
  }>;
  height?: number;
  baseLabels: Array<{
    labelname: string;
    dataKey: string;
    color: string;
  }>;
}

// Custom label inside stacked bars
const PendingLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  if (value > 2) {
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        fontSize="12"
        fill="#fff"
        fontWeight="bold"
        dominantBaseline="middle"
      >
        {`${value}%`}
      </text>
    );
  }
  return null;
};

// Correct X-axis with month + year
const CustomXAxisTick = (props: any) => {
  const { x, y, payload, data } = props;
  const { index } = payload;
  const row = data && data[index];
  const nameString = row?.name || '';
  // 1. Check if the label name string already contains a year extension like "-25" or "-26"
  const hasYearInName = nameString.includes('-');

  let year = '';
  // 2. Only calculate a separate year label row if the name doesn't already have one embedded
  if (!hasYearInName && row?.fullDate) {
    year = `'${new Date(row.fullDate).getFullYear().toString().slice(-2)}`;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#333" fontWeight={500} fontSize={12}>
        {nameString}
      </text>
      {/* 3. Render conditionally: remains completely hidden if year is already visible above */}
      {year ? (
        <text x={0} y={0} dy={32} textAnchor="middle" fill="#666" fontWeight={400} fontSize={11}>
          {year}
        </text>
      ) : null}
    </g>
  );
};

const CustomTooltip = ({ active, payload, baseLabels }: any) => {
  if (!active || !payload || !payload.length) return null;

  const item = payload[0].payload;

  return (
    <div
      style={{
        background: 'white',
        padding: '12px 14px',
        borderRadius: 8,
        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
        border: '1px solid #eee',
        minWidth: 160,
        fontSize: 12,
        position: 'relative',
        zIndex: 100,
      }}
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: 10,
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 6,
          color: '#333',
        }}
      >
        {item.name}
      </div>

      {baseLabels && baseLabels.length > 0
        ? baseLabels.map((label: any) => {
            const rawKey = `${label.dataKey}Raw`;
            const displayValue = item[rawKey] !== undefined ? item[rawKey] : item[label.dataKey] ?? 0;

            return (
              <div key={label.dataKey} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: label.color, fontSize: '14px', lineHeight: 1 }}>●</span>
                <span style={{ fontWeight: 500, color: '#555' }}>{label.labelname}:</span>
                <strong style={{ color: '#111' }}>{displayValue}</strong>
              </div>
            );
          })
        : null}
    </div>
  );
};

const PendingAtBarChart: React.FC<PendingAtBarChartProps> = ({ data, height = 280, baseLabels }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);

  const renderCustomXAxisTick = useCallback((props: any) => <CustomXAxisTick {...props} data={data} />, [data]);

  const isEmpty = !data || data.length === 0;

  const handleBarSegmentClick = (entry: any) => {
    setSelectedPeriod((prev) => (prev === entry.name ? null : entry.name));
  };

  return (
    <>
      <Box sx={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={isEmpty ? [{ name: '', completed: 0, pendingClient: 0, pendingDM: 0, pendingConsultant: 0 }] : data}
            margin={{ top: 10, right: 10, left: -10, bottom: 15 }} // Increased bottom margin slightly for spacing
          >
            {isEmpty && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis dataKey="name" tick={renderCustomXAxisTick} interval={0} />
            <YAxis tick={{ fill: '#333', fontWeight: 500, fontSize: 14 }} />

            <Tooltip content={<CustomTooltip baseLabels={baseLabels} />} cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }} />

            {baseLabels?.map((label: any) => (
              <Bar key={label.dataKey} dataKey={label.dataKey} stackId="a" fill={label.color}>
                {(isEmpty ? [] : data).map((entry) => {
                  const isSelected = selectedPeriod === entry.name;
                  const targetOpacity = selectedPeriod && !isSelected ? 0.2 : 1;

                  return (
                    <Cell
                      key={`${entry.name}-${label.dataKey}`}
                      onClick={() => handleBarSegmentClick(entry)}
                      style={{
                        opacity: targetOpacity,
                        cursor: 'pointer',
                        transition: 'opacity 0.25s ease-in-out',
                      }}
                    />
                  );
                })}
                <LabelList dataKey={label.dataKey} content={PendingLabel} />
              </Bar>
            ))}

            {isEmpty && (
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize={18} fill="#999">
                No data found
              </text>
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        {baseLabels?.map((label: any) => (
          <LegendDot key={label.dataKey} color={label.color} label={label.labelname} />
        ))}
      </Box>
    </>
  );
};

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ width: 16, height: 16, bgcolor: color, borderRadius: '50%' }} />
    <span style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>{label}</span>
  </Box>
);

export default PendingAtBarChart;
